/**
 * LocalNav (TMMPDM-0100): the partner side menu, shared. A labelled nav, one list per group,
 * `aria-current="page"` on the open item. Stacks under the panel below `md`.
 */
import { Box, List, ListItem, ListItemButton, ListItemText, ListSubheader } from '@mui/material';
import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

export interface LocalNavItem {
  key: string;
  label: string;
  to: string;
  /** History state the link carries. Omitted when the item has no state of its own. */
  state?: unknown;
  dot?: boolean;
  /** Appended to the accessible name when `dot` is set (the visible label stays the item text). */
  dotLabel?: string;
}

export interface LocalNavGroup {
  key: string;
  label: string;
  items: LocalNavItem[];
}

export interface LocalNavProps {
  ariaLabel: string;
  groups: LocalNavGroup[];
  activeKey: string;
  onNavigate?: (key: string) => void;
  /**
   * Prefix for each group's `aria-labelledby` id. The partner page keeps `partner-group`
   * so its existing queries still find the lists.
   */
  groupIdPrefix?: string;
  children: ReactNode;
}

export function LocalNav({
  ariaLabel,
  groups,
  activeKey,
  onNavigate,
  groupIdPrefix = 'local-nav-group',
  children,
}: LocalNavProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        gap: 3,
      }}
    >
      <Box
        component="nav"
        aria-label={ariaLabel}
        sx={{ flex: '1 1 200px', maxWidth: { md: 240 }, width: { xs: '100%', md: 'auto' } }}
      >
        <List dense disablePadding>
          {groups.map((group) => (
            <Box component="li" key={group.key} sx={{ listStyle: 'none' }}>
              <List dense disablePadding aria-labelledby={`${groupIdPrefix}-${group.key}`}>
                <ListSubheader
                  id={`${groupIdPrefix}-${group.key}`}
                  disableSticky
                  sx={{ lineHeight: 2.5, bgcolor: 'transparent' }}
                >
                  {group.label}
                </ListSubheader>
                {group.items.map((item) => {
                  const selected = item.key === activeKey;
                  const dot = item.dot === true;
                  return (
                    <ListItem key={item.key} disablePadding>
                      <ListItemButton
                        component={RouterLink}
                        to={item.to}
                        {...('state' in item ? { state: item.state } : {})}
                        selected={selected}
                        {...(selected ? { 'aria-current': 'page' as const } : {})}
                        {...(dot && item.dotLabel
                          ? { 'aria-label': `${item.label}${item.dotLabel}` }
                          : {})}
                        onClick={() => onNavigate?.(item.key)}
                        sx={{
                          borderRadius: 1,
                          '&.Mui-selected': { fontWeight: 600 },
                        }}
                      >
                        <ListItemText
                          primary={
                            <>
                              {item.label}
                              {dot ? (
                                <Box
                                  component="span"
                                  aria-hidden="true"
                                  sx={{
                                    display: 'inline-block',
                                    width: 8,
                                    height: 8,
                                    ml: 0.75,
                                    verticalAlign: 'middle',
                                    borderRadius: '50%',
                                    bgcolor: 'warning.main',
                                  }}
                                />
                              ) : null}
                            </>
                          }
                          slotProps={{
                            primary: { sx: { fontWeight: selected ? 600 : 400 } },
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          ))}
        </List>
      </Box>
      <Box sx={{ flex: '999 1 560px', minWidth: 0 }}>{children}</Box>
    </Box>
  );
}
